import ApiService from './ApiService'
import axios from '../config/axios'

class FashionApiService {
  private static readonly BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders`
  private static readonly ORDERS_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders`
  private static readonly PRODUCTION_FLOW_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow`
  private static readonly FASHION_PRODUCTION_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/fashion-production`
  private static readonly STEEL_PRODUCTS_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-products`

  static async get(endpoint: string, params?: any, config?: any) {
    const url = `${this.BASE_URL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`
    return ApiService.get(url, params, config)
  }

  static async post(endpoint: string, data?: any, config?: any) {
    const url = `${this.BASE_URL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`
    return ApiService.post(url, data, config)
  }

  static async put(endpoint: string, data?: any, config?: any) {
    const url = `${this.BASE_URL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`
    return ApiService.put(url, data, config)
  }

  static async patch(endpoint: string, data?: any, config?: any) {
    const url = `${this.BASE_URL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`
    return ApiService.patch(url, data, config)
  }

  static async delete(endpoint: string, config?: any) {
    const url = `${this.BASE_URL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`
    return ApiService.delete(url, config)
  }

  static async getFashionOrders(page: number = 0, size: number = 10) {
    const pageOneBased = page + 1;
    const url = `${this.ORDERS_URL}/order/paginated?page=${pageOneBased}&limit=${size}&onlyActive=true`
    return ApiService.get(url)
  }

  static async getFashionOrdersCount() {
    const url = `${this.ORDERS_URL}/order/paginated?page=1&limit=1&onlyActive=true`
    return ApiService.get(url)
  }

  static async getFashionProducts(
    page: number,
    size: number,
    options?: {
      status?: boolean
      search?: string
      colecao?: string
      familia?: string
      episodio?: string
    },
  ) {
    const params = new URLSearchParams({
      page: String(page + 1),
      limit: String(size),
    })
    if (options?.search) {
      params.set('search', options.search)
    }
    const url = `${this.STEEL_PRODUCTS_URL}/product?${params.toString()}`
    return ApiService.get(url)
  }

  static async updateFashionProduct(productId: number, data: any) {
    const url = `${this.STEEL_PRODUCTS_URL}/product/${productId}/fashion`
    return ApiService.patch(url, data)
  }

  static async getFamilyColors(familyId: number) {
    return this.get(`/fashion-product/families/${familyId}/colors`)
  }

  static async searchFamilies(name: string) {
    const url = `${this.STEEL_PRODUCTS_URL}/product/families?search=${encodeURIComponent(name)}`
    return ApiService.get(url)
  }

  static async searchGroups(name: string) {
    return this.get(`/fashion-product/groups?name=${encodeURIComponent(name)}`)
  }

  static async getProductionOrders(page: number = 0, size: number = 10, filters?: string) {
    const skip = page * size;
    let url = `${this.PRODUCTION_FLOW_URL}/service?skip=${skip}&quantity=${size}`
    if (filters) {
      url += `&filters=${encodeURIComponent(filters)}`
    }
    return ApiService.get(url)
  }

  static async getProductionOrdersCount(filters?: string) {
    let url = `${this.PRODUCTION_FLOW_URL}/service/count`
    if (filters) {
      url += `?filters=${encodeURIComponent(filters)}`
    }
    return ApiService.get(url)
  }

  static async getProductionOrderById(orderId: number) {
    const url = `${this.FASHION_PRODUCTION_URL}/${orderId}`
    return ApiService.get(url)
  }

  static async getProductionOrderPdfData(orderId: number) {
    const url = `${this.BASE_URL}/fashion-production-orders/${orderId}/pdf-data`
    return ApiService.get(url)
  }

  static async updateProductionOrder(orderId: number, data: any) {
    const url = `${this.PRODUCTION_FLOW_URL}/service/${orderId}`
    return ApiService.patch(url, data)
  }

  static async deleteProductionOrders(ids: number[]) {
    await Promise.all(
      ids.map(id => ApiService.delete(`${this.PRODUCTION_FLOW_URL}/service/${id}`))
    );
    return { status: 'ok' };
  }

  static async generateProductionOrdersFromOrder(orderId: number) {
    const url = `${this.FASHION_PRODUCTION_URL}/generate-from-order/${orderId}`
    return ApiService.post(url)
  }

  static async splitProductionOrder(serviceId: number, orderIds: number[]) {
    const url = `${this.FASHION_PRODUCTION_URL}/${serviceId}/split`
    return ApiService.post(url, { orderIds })
  }

  static async getUsers(role?: string) {
    const endpoint = role ? `/users?role=${encodeURIComponent(role)}` : '/users'
    return this.get(endpoint)
  }

  static async getStages() {
    return this.get('/stages')
  }

  static async getPaymentConditions() {
    return { data: [] }
  }

  static async getShippingTypes() {
    return { data: [] }
  }

  static async getMaterialAvailability(orderId: number) {
    const url = `${this.FASHION_PRODUCTION_URL}/${orderId}/material-availability`
    return ApiService.get(url)
  }

  static async getFashionProductById(productId: number) {
    const url = `${this.STEEL_PRODUCTS_URL}/product/${productId}`
    return ApiService.get(url)
  }

  static async startFashionProduction(orderId: number) {
    const url = `${this.FASHION_PRODUCTION_URL}/${orderId}/start-production`
    return ApiService.post(url, {}, { timeout: 60000 })
  }

  static async revertFashionProductionStart(orderId: number) {
    const url = `${this.FASHION_PRODUCTION_URL}/${orderId}/revert-start-production`
    return ApiService.post(url, {}, { timeout: 60000 })
  }

  static async updateProductionOrderItems(
    orderId: number,
    items: { fashionOrderId: number; quantityForThisProduction: number }[],
  ) {
    const url = `${this.FASHION_PRODUCTION_URL}/${orderId}/items`
    return ApiService.patch(url, { items })
  }

  static async getSalesStockItems(filters?: Record<string, string>) {
    const params = new URLSearchParams()
    if (filters) {
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params.set(k, v)
      })
    }
    const qs = params.toString()
    return this.get(`/sales-stock${qs ? '?' + qs : ''}`)
  }

  static async getSalesStockStats() {
    return this.get('/sales-stock/stats')
  }

  static async getSalesStockFilterOptions() {
    return this.get('/sales-stock/filter-options')
  }

  static async getSalesStockQrCode(itemId: number) {
    return this.get(`/sales-stock/${itemId}/qr`)
  }

  static async updateSalesStockItemStatus(itemId: number, status: string) {
    return this.patch(`/sales-stock/${itemId}`, { status })
  }

  static async revertFashionSale(orderId: number) {
    const url = `${this.BASE_URL}/fashion-production-internal/revert-sale/${orderId}`
    return ApiService.post(url, {}, { timeout: 60000 })
  }
}

export default FashionApiService